// Janelas de antecedência dos alertas (P-12).
//
// Antes disto o número 30 aparecia em quatro lugares: `painel_dias_de_alerta()`
// no banco e três cópias no React — `shared-data.ts`, a tela de funcionários do
// condomínio e a aba de funcionários. Mudar o prazo pela tela de administração
// não alcançava nenhum deles, porque a tela nem gravava.
//
// A configuração vive em `notificacao_parametros` (migration 0028) e chega pelo
// catálogo, cacheada por sessão como o resto. O banco continua sendo quem
// aplica a regra onde ela é apurada de verdade (`vw_treinamentos_situacao`,
// `painel_da_conta()`); aqui ficam os casos em que a tela classifica sozinha.

import {
  invalidarCatalogo,
  useCatalogo,
  type CanalNotificacao,
  type ParametroNotificacao,
} from '@/lib/catalogo';
import { supabase } from '@/lib/supabase';

/**
 * Fallback explícito, no sentido da ADR-0011.
 *
 * Não é a configuração: é o que evita que uma falha ao carregar o catálogo
 * transforme "a vencer" em "vigente" e apague o alerta sem ninguém perceber.
 * Mesmo valor que o banco usa pelo mesmo motivo.
 */
export const JANELA_PADRAO_DIAS = 30;

export type ChaveDeAlerta = 'treinamento' | 'alvara' | 'manutencao' | 'aviso_previo';

export function janelaDeAlerta(parametros: ParametroNotificacao[], chave: ChaveDeAlerta): number {
  return parametros.find((p) => p.chave === chave)?.dias ?? JANELA_PADRAO_DIAS;
}

/** Janela configurada para um tipo de alerta, em dias. */
export function useJanelaDeAlerta(chave: ChaveDeAlerta): number {
  const { itens } = useCatalogo<ParametroNotificacao>('notificacao_parametros');
  return janelaDeAlerta(itens, chave);
}

/**
 * Grava os prazos e os canais alterados.
 *
 * Só `update`: as chaves são fixadas por migration, junto do gatilho que vai
 * consumi-las — parâmetro novo não nasce pela tela. Quem pode escrever é o
 * superusuário, e quem decide isso são as policies; aqui o erro do banco sobe.
 *
 * A faixa aceita é conferida pela constraint `notificacao_parametros_dias_check`,
 * então um valor fora dela volta como erro mesmo que a tela seja contornada.
 */
export async function salvarNotificacoes(
  parametros: { chave: string; dias: number }[],
  canais: { chave: string; ativo: boolean }[],
): Promise<void> {
  const agora = new Date().toISOString();

  for (const p of parametros) {
    const { error } = await supabase
      .from('notificacao_parametros')
      .update({ dias: p.dias, editado_em: agora })
      .eq('chave', p.chave);
    if (error) throw new Error(error.message);
  }

  for (const c of canais) {
    const { error } = await supabase
      .from('notificacao_canais')
      .update({ ativo: c.ativo, editado_em: agora })
      .eq('chave', c.chave);
    if (error) throw new Error(error.message);
  }

  // O gatilho da migration 0023 já invalida o cache do Redis pela versão; esta
  // é a contraparte da sessão, que guarda o catálogo em memória.
  invalidarCatalogo('notificacao_parametros');
  invalidarCatalogo('notificacao_canais');
}

export type { CanalNotificacao, ParametroNotificacao };
